import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Link } from "react-router-dom";

/**
 * Magnetic button — subtly pulls its label toward the cursor.
 * Usable as a Link (to=) or as a plain button (onClick=).
 */
export default function MagneticButton({
  children,
  to,
  href,
  onClick,
  variant = "solid",
  className = "",
  testid,
  ...rest
}) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 150, damping: 15 });
  const sy = useSpring(my, { stiffness: 150, damping: 15 });
  const lx = useTransform(sx, (v) => v * 0.4);
  const ly = useTransform(sy, (v) => v * 0.4);

  const handleMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set(e.clientX - rect.left - rect.width / 2);
    my.set(e.clientY - rect.top - rect.height / 2);
  };
  const handleLeave = () => {
    mx.set(0);
    my.set(0);
  };

  const baseClasses =
    "relative inline-flex items-center justify-center gap-3 px-8 py-4 uppercase tracking-[0.2em] text-xs md:text-sm font-medium border transition-colors";
  const variants = {
    solid:
      "bg-white text-black border-white hover:bg-[#8b0000] hover:text-white hover:border-[#8b0000]",
    ghost:
      "bg-transparent text-white border-white/40 hover:bg-white hover:text-black hover:border-white",
    velvet:
      "bg-[#8b0000] text-white border-[#8b0000] hover:bg-white hover:text-black hover:border-white",
  };

  const inner = (
    <motion.span
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{ x: sx, y: sy }}
      className={`${baseClasses} ${variants[variant] || variants.solid} ${className}`}
      data-testid={testid}
      onClick={onClick}
      {...rest}
    >
      <motion.span style={{ x: lx, y: ly }} className="flex items-center gap-3">
        {children}
      </motion.span>
    </motion.span>
  );

  if (to) return <Link to={to}>{inner}</Link>;
  if (href) return <a href={href} target="_blank" rel="noreferrer">{inner}</a>;
  return inner;
}
